
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { BaseLayout } from '@/components/templates';
import { withUrql } from '@/config/graphql';
import { getArticleUrl, getCategoryUrl } from '@/utils/academy';
import { Box, Container, Flex, Heading, TabList, TabPanel, TabPanels, Tabs, Text, } from '@chakra-ui/react';
import { contentfulClient } from '@cms/client';
import { Breadcrumbs, IndexItem, IndexList, LazyImage, LinkBox, LinkBoxList, } from '@meritcircle/ui/components';
import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import { ArticleCategoriesPageDocument, ArticleCategoriesPageQuery, ArticleCategoryPageDocument, ArticleCategoryPageQuery, } from '@cms/graphql/articleCategoryPage.generated';
import useTranslation from 'next-translate/useTranslation';
import { URLS } from '@/config/urls';
import { useRouter } from 'next/router';
import { canonicalUrl as getCanonicalUrl } from '@/utils/seo';
import { Seo } from '@/components/utils/seo/seo-component';
import { AcadamyCategoryPageProps } from '@/components/features/academy/academy.types';
import { formatReadingTime } from '@/components/features/academy/academy.helpers';
import { getOpenGraphProps } from '@/components/utils/seo/get-open-graph-props';
import { BreadcrumbJsonLd } from 'next-seo';
type PageProps = InferGetStaticPropsType<typeof getStaticProps>;
const Page: NextPage<PageProps> = ({ linkedSeoTags, lastUpdate, title, description, metaTitle, metaDescription, ogImage, noFollow, noIndex, keywords, overviewImage, urlSlug, subCategories, mainCategories, }) => {
    const { t } = useTranslation('academy');
    const router = useRouter();
    const breadcrumbs = [
        { title: t('title'), href: URLS.ACADEMY },
        { title: title, href: getCategoryUrl(urlSlug) },
    ];
    const canonicalUrl = getCanonicalUrl(router);
    return (<>
      {overviewImage && (<Box position="absolute" height="50vh" width="100%" _after={{
                content: '""',
                width: '100%',
                height: '100%',
                position: 'absolute',
                background: 'linear-gradient(1.07deg, #000000 20.53%, rgba(0, 0, 0, 0) 42.04%)'
            }}>
          <LazyImage src={overviewImage.url} placeholderUrl={overviewImage.url} alt={overviewImage.description || overviewImage.title || `${title} image`} position="absolute" objectFit="cover" width="100%" height="100%" maxWidth="none" opacity="0.1"/>
        </Box>)}
      <BaseLayout>
        <Seo title={linkedSeoTags?.title || metaTitle || title} description={linkedSeoTags?.description || metaDescription || description} nofollow={linkedSeoTags?.noFollow || noFollow} noindex={linkedSeoTags?.noIndex || noIndex} keywords={linkedSeoTags?.keywords || keywords} lastUpdate={lastUpdate} canonical={canonicalUrl} openGraph={getOpenGraphProps({
            title: linkedSeoTags?.title || metaTitle || title,
            description: linkedSeoTags?.description || metaDescription || description,
            url: canonicalUrl,
            image: ogImage
        })}/>
        <BreadcrumbJsonLd itemListElements={breadcrumbs.map((breadcrumb, idx) => ({
            position: idx + 1,
            name: breadcrumb.title,
            item: breadcrumb.href
        }))}/>
        <Container pb="2xl">
          <Breadcrumbs breadcrumbs={breadcrumbs} mt={{ base: 'md', md: 'lg' }}/>
          <Box maxWidth={750} pt={{ base: 10, md: 20 }} pb={{ base: 14, md: 24 }}>
            <Heading as="h1" variant="glowing" color="lightPurple" size="2xl" mb={{ base: 4, md: 6 }}>
              {title}
            </Heading>
            <Text>{description}</Text>
          </Box>
          <Tabs flex="1" display="flex" flexDirection={{ base: 'column', md: 'row' }} mb={{ base: 14, md: 30 }} gap={8}>
            <Box flex="1">
              <Heading size="h3" as="h2" display={{ base: 'none', md: 'block' }} mb={8}>
                Learn about
              </Heading>
              <TabList>
                <IndexList>
                  {subCategories.map(({ title }, index) => (<IndexItem key={index}>{title}</IndexItem>))}
                </IndexList>
              </TabList>
            </Box>
            <TabPanels flex="1">
              {subCategories.map(({ childArticlesCollection }, index) => (<TabPanel key={index}>
                  <LinkBoxList flex={1}>
                    {childArticlesCollection.items.map(({ overviewTitle, overviewDescription, urlSlug, readingTime }) => (<LinkBox href={getArticleUrl(urlSlug)} key={urlSlug}>
                          <Heading as="h4" size="h4">
                            {overviewTitle}
                          </Heading>
                          {readingTime && (<Text variant="muted" size="xs" mt={2} textTransform="uppercase">
                              {formatReadingTime(readingTime, t('common:date.minute'), t('common:date.minutes'))}
                            </Text>)}
                          <Text variant="muted" mt={4}>
                            {overviewDescription}
                          </Text>
                        </LinkBox>))}
                  </LinkBoxList>
                </TabPanel>))}
            </TabPanels>
          </Tabs>

          <Flex direction={{ base: 'column', md: 'row' }} gap={8}>
            <Heading size="h3" as="h2" flex={1}>
              {t('section-link-box-list.title')}
            </Heading>
            <LinkBoxList flex={1}>
              {mainCategories.map(({ title, description, urlSlug }, index) => (<LinkBox href={getCategoryUrl(urlSlug)} key={index}>
                  <Heading as="h3" size="h4" mb={4}>
                    {title}
                  </Heading>
                  <Text variant="muted">{description}</Text>
                </LinkBox>))}
            </LinkBoxList>
          </Flex>
        </Container>
      </BaseLayout>
    </>);
};
export const getStaticPaths = async () => {
    return {
        /**
         * We don't build any CMS pages statically. However, next
         * requires you to at least add one param. Therefore this fake page is added,
         * which during build time already resolves to a 404 page. After that
         * all pages are rendered on the server and cached there. Every 30 mins (if the page is hit)
         * the server will revalidate the page.
         */
        paths: [{ params: { slug: 'slug' } }],
        fallback: 'blocking'
    };
};
const getStaticProps: GetStaticProps<AcadamyCategoryPageProps> = async (ctx) => {
    const slug = `${ctx?.params?.slug}`;
    const categoryResponse = await contentfulClient
        .query<ArticleCategoryPageQuery>(ArticleCategoryPageDocument, { slug })
        .toPromise();
    const categoriesResponse = await contentfulClient
        .query<ArticleCategoriesPageQuery>(ArticleCategoriesPageDocument)
        .toPromise();
    const category = categoryResponse?.data?.category.items[0];
    const categories = categoriesResponse?.data?.categories.items;
    if (!category || !categories) {
        return {
            notFound: true
        };
    }
    const { title, description, overviewImage, urlSlug, childCategoriesCollection, linkedSeoTags, metaTitle, metaDescription, ogImage, noFollow, noIndex, keywords, } = category;
    const mainCategories = categories.filter(category => urlSlug !== category.urlSlug);
    return {
        props: {
            linkedSeoTags,
            lastUpdate: `${new Date()}`,
            title,
            description,
            metaTitle,
            metaDescription,
            ogImage,
            noFollow,
            noIndex,
            keywords,
            overviewImage,
            urlSlug,
            subCategories: childCategoriesCollection.items,
            mainCategories
        },
        revalidate: 5 * 60
    };
};
export default withUrql(Page);

    async function __Next_Translate__getStaticProps__1973b3b2dc6__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/academy/category/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1973b3b2dc6__ as getStaticProps }
  